import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '../../context/AppStateContext';
import { ChildProfile } from '../../models/childProfile';
import { ORG_CONFIG_DEFAULTS } from '../../models/orgConfig';
import { StaffProfile } from '../../models/staffProfile';
import { UserDataService } from '../../services/userDataService';
import { ScheduleDataService } from '../../services/scheduleDataService';
import { AttendanceDataService, AttendanceRecord } from '../../services/attendanceDataService';
import { ScheduleSession } from '../schedule/SessionDetailPanel';
import { toDateString } from '../../services/dateUtils';
import { occursOnDate, weekdayOf } from '../schedule/scheduleOccurrences';
import ChildAvatar from '../ChildAvatar';
import AttendanceConfirmModal, { AttendanceActionKind, AttendanceTiming } from './AttendanceConfirmModal';
import AttendedTimesModal from './AttendedTimesModal';
import UnscheduledSessionModal from './UnscheduledSessionModal';
import ConfirmationModal from '../ConfirmationModal';
import { composeNoShowComment } from '../../services/attendanceReasons';
import './dashboard.scss';
import { getSelectedOrgId } from '../../services/selectedOrg';
import { useConnectivity } from '../../hooks/useConnectivity';

// Default on-time grace window — used as the fallback when org config
// hasn't loaded yet. Within ±this many minutes of the scheduled time,
// the chip scale is suppressed and a single tap-in / tap-out button is
// shown instead. Org admins override this via the Organisation editor;
// see data.organisation_config.attendance_grace_minutes (migration 014).
const DEFAULT_GRACE_MINUTES = ORG_CONFIG_DEFAULTS.attendanceGraceMinutes;

// A session that was *never* signed in stops being eligible for NOW once
// its scheduled end is more than this many minutes in the past. After that
// it ages out of NOW into the action-required tray so the user can record
// what actually happened. Sessions that *have* been signed in stay in NOW
// indefinitely until the user signs them out — matching the iOS dashboard
// behaviour where an in-progress session never silently drops off the
// "current" list.
const NOW_STALE_GRACE_MIN = 30;

// NEXT UP visibility window. Sessions further than this many minutes from
// their scheduled start are not surfaced in the NEXT UP section — they
// only appear on the today schedule strip. Matches the iOS dashboard's
// "anything starting in the next 30 minutes" copy.
const NEXT_UP_WINDOW_MIN = 30;

// How far back to scan for past sessions that have no attendance recorded.
// Anything found in this window that's neither logged nor cancelled is
// surfaced as "Action required" at the top of the dashboard so the user
// can record what actually happened (or cancel the session as a one-off
// if it shouldn't have run). Kept short — a week — so the tray stays a
// genuine to-do rather than a long-tail backlog.
const LOOKBACK_DAYS = 7;

// Cadence for the live tick (drives slot recomputation + countdowns) and for
// silent server polling (picks up schedule edits and attendance entered by
// other users). The poll is gated by an "in-edit" ref so an open confirm
// modal is never disrupted mid-typing.
const TICK_MS = 20_000;
const REFRESH_MS = 20_000;


const JUST_ME_STORAGE_KEY = 'dashboardJustMe';

// Sessions the user has cleared from the Needs-attention tray with
// "Dismiss all". Purely a view preference — nothing is written to the
// server and the attendance stays unrecorded, so the same gaps remain
// visible (and actionable) on the Attendance page. Scoped per org because
// the tray is per org; a user who switches orgs shouldn't inherit the
// other one's dismissals.
const ATTENTION_DISMISSED_KEY_PREFIX = 'dashboardAttentionDismissed';

const attentionDismissedKey = () =>
  `${ATTENTION_DISMISSED_KEY_PREFIX}:${getSelectedOrgId() ?? 'none'}`;

function loadDismissedAttention(): Set<string> {
  try {
    const raw = localStorage.getItem(attentionDismissedKey());
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter(k => typeof k === 'string')) : new Set();
  } catch {
    // Corrupt or unreadable (private mode, cleared storage) — start clean
    // rather than hiding the tray behind a storage error.
    return new Set();
  }
}

function saveDismissedAttention(keys: Set<string>) {
  try {
    localStorage.setItem(attentionDismissedKey(), JSON.stringify(Array.from(keys)));
  } catch {
    // Storage full or blocked — the dismissal still holds for this session.
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function timeToMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function nowToHHMM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

type Timing = 'on-time' | 'early' | 'late';
function classifyTiming(scheduledMins: number, nowMins: number, grace: number): Timing {
  const delta = nowMins - scheduledMins;
  if (Math.abs(delta) <= grace) return 'on-time';
  return delta < 0 ? 'early' : 'late';
}

// Mirrors the CTA timing palette so status ticks read with the same colour
// language as the buttons that produced them.
const TIMING_COLOURS: Record<Timing, string> = {
  'on-time': '#7cb342',
  'early':   '#e8a92e',
  'late':    '#ea7c30',
};

// Timing classifier exists; per-timing visuals are now driven by CSS modifier
// classes on the action buttons (.dash-now__cta--on-time / --early / --late).

function fmtCountdown(diffMin: number): string {
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `in ${diffMin} min`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
}

// Human-readable date label for the action-required tray. Today/Yesterday/
// "Mon 4 May" etc. Keeping this self-contained (no Intl parsing of foreign
// strings) so it stays cheap and matches the dashboard's English copy.
function formatDateRelative(dateStr: string, today: string): string {
  if (dateStr === today) return 'Today';
  const d = new Date(dateStr + 'T00:00');
  const t = new Date(today + 'T00:00');
  const diffDays = Math.round((t.getTime() - d.getTime()) / 86_400_000);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(d);
  return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
}

// Date span shown on the collapsed Needs-attention bar, e.g. "Yesterday" or
// "Mon 28 Jul – Fri 1 Aug". Orienting information rather than instruction:
// how far back the backlog reaches is what decides whether it's urgent.
function formatDateSpan(dates: string[], today: string): string {
  if (dates.length === 0) return '';
  const sorted = [...dates].sort();
  const oldest = sorted[0];
  const newest = sorted[sorted.length - 1];
  if (oldest === newest) return formatDateRelative(oldest, today);
  const short = (s: string) =>
    new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
      .format(new Date(s + 'T00:00'));
  return `${short(oldest)} – ${short(newest)}`;
}

// YYYY-MM-DD for a given Date in local time. Mirrors the project's
// toDateString helper but stays local to avoid importing.
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Types ──────────────────────────────────────────────────────────────────

interface Props {
  childProfiles: ChildProfile[];
  onJumpToAttendance?: () => void;
  /** Navigate to the Posts screen filtered to the given child. */
  onViewChildPosts?: (childId: string) => void;
}

type Slot = 'now' | 'next' | 'other' | 'unactioned';

interface DashboardSession {
  session: ScheduleSession;
  // Calendar date this projection refers to. For today's slots this equals
  // `today`; for unactioned past sessions it's the actual past date so
  // the recorded attendance lands on the correct day.
  attendanceDate: string;
  scheduledStart: string;       // 'HH:MM'
  scheduledEnd: string;
  startMins: number;
  endMins: number;
  attendance: AttendanceRecord | null;
  slot: Slot;
}

// Stable identity for one unactioned row, used both as the in-flight save
// key and as the persisted "dismissed" key. Built from server ids so it
// survives a reload (the local `session.id` is reassigned on each hydrate).
const attentionRowKey = (p: DashboardSession) =>
  `${p.session.serverId}-${p.session.childId}-${p.attendanceDate}`;

interface ChildInfoView {
  profile: ChildProfile | null;
  name: string;
  colour: string;
  initials: string;
}

// ── Hoisted card components ────────────────────────────────────────────────
//
// These are intentionally defined OUTSIDE the Dashboard component. Defining
// them inside meant every parent re-render (e.g. the live tick or a silent
// server refresh) gave them new component identities, which forced React to
// unmount + remount them and discard the local modal state — wiping out a
// no-show reason mid-typing. Hoisted, they keep their internal state across
// parent renders.


// Shared sign-in / sign-out / no-show action logic for both the NOW and the
// UP NEXT cards. Mirrors the iOS SessionCard.actionRow, where the very same
// action set is offered regardless of whether the card sits in the "Now" or
// "Up next" section — so a user can "Sign in (Early)" up to NEXT_UP_WINDOW_MIN
// before the scheduled start, and (since an early sign-in keeps the session in
// the UP NEXT slot until its start) can still sign out from there too.
interface CardActions {
  tappedIn: boolean;
  tappedOut: boolean;
  tapInTiming: Timing;
  tapOutTiming: Timing;
  tapInLabel: string;
  tapOutLabel: string;
  onSignInClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSignOutClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNoShowClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** The confirmation popup element — render once inside the card. */
  modal: React.ReactNode;
}

function useCardActions(
  p: DashboardSession,
  childName: string,
  now: Date,
  graceMinutes: number,
  recordKey: string,
  onTapIn: (p: DashboardSession, time: string, comment: string | null) => void,
  onTapOut: (p: DashboardSession, time: string, comment: string | null) => void,
  onNoShow: (p: DashboardSession, comment: string | null) => void,
  onBusyChange: (key: string, busy: boolean) => void,
): CardActions {
  const tappedIn = !!p.attendance?.actualArrival;
  const tappedOut = !!p.attendance?.actualDeparture;
  const nowMins = now.getHours() * 60 + now.getMinutes();

  const tapInTiming = classifyTiming(p.startMins, nowMins, graceMinutes);
  const tapOutTiming = classifyTiming(p.endMins, nowMins, graceMinutes);

  const tapInLabel = tapInTiming === 'on-time'
    ? 'Sign in'
    : `Sign in (${tapInTiming === 'early' ? 'Early' : 'Late'})`;
  const tapOutLabel = tapOutTiming === 'on-time'
    ? 'Sign out'
    : `Sign out (${tapOutTiming === 'early' ? 'Early' : 'Late'})`;

  // Pending action triggers the confirmation popup. Only used when the action
  // needs a comment (early/late) or a reason (no-show); on-time sign-in/out
  // commits immediately. anchorRect positions the popup below the trigger.
  type Pending =
    | { kind: 'tap-in'; timing: AttendanceTiming; anchorRect: DOMRect }
    | { kind: 'tap-out'; timing: AttendanceTiming; anchorRect: DOMRect }
    | { kind: 'no-show'; anchorRect: DOMRect };
  const [pending, setPending] = useState<Pending | null>(null);

  // Report busy state to the parent so the silent refresh can pause while the
  // user is mid-edit. Cleanup clears the flag if the card unmounts.
  useEffect(() => {
    onBusyChange(recordKey, pending !== null);
    return () => { onBusyChange(recordKey, false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, recordKey]);

  const onSignInClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (tapInTiming === 'on-time') {
      onTapIn(p, p.scheduledStart, null);
    } else {
      setPending({
        kind: 'tap-in',
        timing: tapInTiming,
        anchorRect: e.currentTarget.getBoundingClientRect(),
      });
    }
  };

  const onSignOutClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (tapOutTiming === 'on-time') {
      onTapOut(p, p.scheduledEnd, null);
    } else {
      setPending({
        kind: 'tap-out',
        timing: tapOutTiming,
        anchorRect: e.currentTarget.getBoundingClientRect(),
      });
    }
  };

  const onNoShowClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setPending({ kind: 'no-show', anchorRect: e.currentTarget.getBoundingClientRect() });

  const handleConfirm = (comment: string | null, reason: string | null, time: string | null) => {
    if (!pending) return;
    if (pending.kind === 'tap-in') {
      // `time` is the actual arrival the user confirmed (defaults to now,
      // editable in the modal; scheduled start when re-marked on-time).
      onTapIn(p, time ?? p.scheduledStart, comment);
    } else if (pending.kind === 'tap-out') {
      onTapOut(p, time ?? p.scheduledEnd, comment);
    } else {
      const merged = reason ? composeNoShowComment(reason, comment ?? '') : (comment || null);
      onNoShow(p, merged);
    }
    setPending(null);
  };

  const modal = (
    <AttendanceConfirmModal
      anchorRect={pending?.anchorRect ?? null}
      kind={(pending?.kind ?? 'tap-in') as AttendanceActionKind}
      timing={pending && pending.kind !== 'no-show' ? pending.timing : undefined}
      childName={childName}
      defaultTime={nowToHHMM(now)}
      scheduledTime={pending?.kind === 'tap-out' ? p.scheduledEnd : p.scheduledStart}
      // Keep the recorded times orderable: a sign-out can't precede the
      // recorded arrival (DB enforces departure >= arrival), and an early
      // sign-in can't land after an already-recorded departure.
      minTime={pending?.kind === 'tap-out' ? (p.attendance?.actualArrival ?? undefined) : undefined}
      maxTime={pending?.kind === 'tap-in' ? (p.attendance?.actualDeparture ?? undefined) : undefined}
      onConfirm={handleConfirm}
      onCancel={() => setPending(null)}
    />
  );

  return {
    tappedIn, tappedOut, tapInTiming, tapOutTiming, tapInLabel, tapOutLabel,
    onSignInClick, onSignOutClick, onNoShowClick, modal,
  };
}

interface NowCardProps {
  p: DashboardSession;
  ci: ChildInfoView;
  now: Date;
  /** ± minutes around scheduled time treated as on-time. Comes from org
   *  config; falls back to DEFAULT_GRACE_MINUTES while loading. */
  graceMinutes: number;
  recordKey: string;
  onTapIn: (p: DashboardSession, time: string, comment: string | null) => void;
  onTapOut: (p: DashboardSession, time: string, comment: string | null) => void;
  onNoShow: (p: DashboardSession, comment: string | null) => void;
  onBusyChange: (key: string, busy: boolean) => void;
  /** Shown once the child is signed in; navigates to their Posts. */
  onViewPosts?: () => void;
}

const NowCard: React.FC<NowCardProps> = ({
  p, ci, now, graceMinutes, recordKey, onTapIn, onTapOut, onNoShow, onBusyChange, onViewPosts,
}) => {
  const {
    tappedIn, tappedOut, tapInTiming, tapOutTiming, tapInLabel, tapOutLabel,
    onSignInClick, onSignOutClick, onNoShowClick, modal,
  } = useCardActions(
    p, ci.name, now, graceMinutes, recordKey, onTapIn, onTapOut, onNoShow, onBusyChange,
  );

  return (
    <div className="dash-now" style={{ ['--child-color' as string]: ci.colour } as React.CSSProperties}>
      <div className="dash-now__time">{p.scheduledStart}</div>
      <div className="dash-now__divider" />
      <div className="dash-now__body">
        <div className="dash-now__avatar">
          {ci.profile ? (
            <ChildAvatar
              child={ci.profile}
              size={56}
              imageUrl={ci.profile.profile_image_url}
              showBadge={true}
            />
          ) : (
            <span>{ci.initials}</span>
          )}
        </div>
        <div className="dash-now__bodytext">
          <div className="dash-now__topline">
            <span className="dash-now__pill">Now</span>
            <span className="dash-now__range">{p.scheduledStart}–{p.scheduledEnd}</span>
          </div>
          <h4 className="dash-now__name">{ci.name}</h4>
        </div>
      </div>

      {!tappedIn && (
        <button
          type="button"
          className={`dash-now__cta dash-now__cta--${tapInTiming}`}
          data-help="dash.sign-in-out"
          onClick={onSignInClick}
        >
          <i className="fas fa-feather" /> {tapInLabel}
        </button>
      )}

      {tappedIn && !tappedOut && (
        <button
          type="button"
          className={`dash-now__cta dash-now__cta--${tapOutTiming}`}
          data-help="dash.sign-in-out"
          onClick={onSignOutClick}
        >
          <i className="fas fa-door-open" /> {tapOutLabel}
        </button>
      )}

      {p.session.locationIds.length > 0 && (
        <div className="dash-now__rightmeta">
          <div className="dash-now__meta">
            <span className="dash-now__metalabel">Location:</span>
            <span>
              {p.session.locationIds.length === 1
                ? '1 classroom'
                : `${p.session.locationIds.length} classrooms`}
            </span>
          </div>
        </div>
      )}

      {!tappedIn && (
        <button
          type="button"
          className="dash-now__noshow"
          data-help="dash.no-show"
          onClick={onNoShowClick}
        >
          <i className="fas fa-user-slash" /> No-show
        </button>
      )}

      {tappedIn && onViewPosts && (
        <button
          type="button"
          className="dash-viewposts"
          onClick={onViewPosts}
        >
          <i className="fas fa-images" /> View posts
        </button>
      )}

      {tappedIn && !tappedOut && (() => {
        const arrivalTiming: Timing = p.attendance?.actualArrival
          ? classifyTiming(p.startMins, timeToMins(p.attendance.actualArrival), graceMinutes)
          : 'on-time';
        return (
          <div className="dash-now__taggedrow">
            <i className="fas fa-check-circle" style={{ color: TIMING_COLOURS[arrivalTiming] }} />
            Signed in at <strong>{p.attendance?.actualArrival}</strong>
            {p.attendance?.arrivalComment && (
              <span className="dash-now__taggedrow-note">— {p.attendance.arrivalComment}</span>
            )}
          </div>
        );
      })()}

      {tappedIn && tappedOut && (() => {
        const arrivalTiming: Timing = p.attendance?.actualArrival
          ? classifyTiming(p.startMins, timeToMins(p.attendance.actualArrival), graceMinutes)
          : 'on-time';
        const departureTiming: Timing = p.attendance?.actualDeparture
          ? classifyTiming(p.endMins, timeToMins(p.attendance.actualDeparture), graceMinutes)
          : 'on-time';
        // Pick the more attention-worthy of the two (late > early > on-time)
        // so the icon hints at the most notable variance.
        const rank: Record<Timing, number> = { 'on-time': 0, early: 1, late: 2 };
        const overall: Timing =
          rank[departureTiming] >= rank[arrivalTiming] ? departureTiming : arrivalTiming;
        return (
          <div className="dash-now__taggedrow">
            <i className="fas fa-check-double" style={{ color: TIMING_COLOURS[overall] }} />
            Attended {p.attendance?.actualArrival}—{p.attendance?.actualDeparture}
          </div>
        );
      })()}

      {modal}
    </div>
  );
};

interface NextCardProps {
  p: DashboardSession;
  ci: ChildInfoView;
  now: Date;
  graceMinutes: number;
  recordKey: string;
  onTapIn: (p: DashboardSession, time: string, comment: string | null) => void;
  onTapOut: (p: DashboardSession, time: string, comment: string | null) => void;
  onNoShow: (p: DashboardSession, comment: string | null) => void;
  onBusyChange: (key: string, busy: boolean) => void;
  /** Shown once the child is signed in (early); navigates to their Posts. */
  onViewPosts?: () => void;
}

const NextCard: React.FC<NextCardProps> = ({
  p, ci, now, graceMinutes, recordKey, onTapIn, onTapOut, onNoShow, onBusyChange, onViewPosts,
}) => {
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const minsToStart = p.startMins - nowMins;

  // Same action set as the NOW card (iOS parity): the user can sign in early,
  // mark a no-show, or — if they've already signed in early — sign out, all
  // from the UP NEXT section.
  const {
    tappedIn, tappedOut, tapInTiming, tapOutTiming, tapInLabel, tapOutLabel,
    onSignInClick, onSignOutClick, onNoShowClick, modal,
  } = useCardActions(
    p, ci.name, now, graceMinutes, recordKey, onTapIn, onTapOut, onNoShow, onBusyChange,
  );

  return (
    <div className="dash-next" style={{ ['--child-color' as string]: ci.colour } as React.CSSProperties}>
      <div className="dash-next__time">{p.scheduledStart}</div>
      <div className="dash-next__divider" />
      <div className="dash-next__body">
        <div className="dash-next__avatar">
          {ci.profile ? (
            <ChildAvatar
              child={ci.profile}
              size={56}
              imageUrl={ci.profile.profile_image_url}
              showBadge={true}
            />
          ) : (
            <span>{ci.initials}</span>
          )}
        </div>
        <div className="dash-next__bodytext">
          <span className="dash-next__pill">Up next</span>
          <h4 className="dash-next__name">{ci.name}</h4>
          <span className="dash-next__countdown">{fmtCountdown(minsToStart)}</span>
          {tappedIn && !tappedOut && p.attendance?.actualArrival && (
            <span className="dash-next__signedin">
              <i className="fas fa-check-circle" /> Signed in at <strong>{p.attendance.actualArrival}</strong>
            </span>
          )}
        </div>
      </div>
      {!tappedIn && (
        <button
          type="button"
          className={`dash-next__cta dash-next__cta--${tapInTiming}`}
          onClick={onSignInClick}
        >
          <i className="fas fa-feather" /> {tapInLabel}
        </button>
      )}

      {tappedIn && !tappedOut && (
        <button
          type="button"
          className={`dash-next__cta dash-next__cta--${tapOutTiming}`}
          onClick={onSignOutClick}
        >
          <i className="fas fa-door-open" /> {tapOutLabel}
        </button>
      )}

      {p.session.locationIds.length > 0 && (
        <div className="dash-next__rightmeta">
          <div className="dash-next__meta">
            <span className="dash-next__metalabel">Location:</span>
            <span>
              {p.session.locationIds.length === 1
                ? '1 classroom'
                : `${p.session.locationIds.length} classrooms`}
            </span>
          </div>
        </div>
      )}

      {!tappedIn && (
        <button
          type="button"
          className="dash-next__noshow"
          onClick={onNoShowClick}
        >
          <i className="fas fa-user-slash" /> No-show
        </button>
      )}

      {tappedIn && onViewPosts && (
        <button
          type="button"
          className="dash-viewposts"
          onClick={onViewPosts}
        >
          <i className="fas fa-images" /> View posts
        </button>
      )}
      {modal}
    </div>
  );
};

interface ActionRequiredCardProps {
  p: DashboardSession;
  ci: ChildInfoView;
  /** Pre-formatted relative date label (e.g. "Yesterday", "Mon 4 May"). */
  dateLabel: string;
  recordKey: string;
  onAttended: (
    p: DashboardSession,
    arrival: string,
    departure: string,
    arrivalComment: string | null,
    departureComment: string | null,
  ) => void;
  onNoShow: (p: DashboardSession, comment: string | null) => void;
  onDismiss: (p: DashboardSession, reason: string | null) => void;
  onBusyChange: (key: string, busy: boolean) => void;
}

// Row in the "Action required" tray for a past session that has no attendance
// recorded (or a partial sign-in awaiting sign-out). Three CTAs:
//   • Attended — opens the AttendedTimesModal so the user can confirm or
//     edit the arrival + departure times (and per-side comments) before
//     saving. Mirrors the time controls used by the Attendance page's
//     AttendanceDetailPanel.
//   • No-show — opens the existing reason-picker modal.
//   • Cancel session — opens the modal with the same cancellation reason
//     dropdown as the Weekly Schedule and writes a one-off cancellation
//     exception so the row clears.
const ActionRequiredCard: React.FC<ActionRequiredCardProps> = ({
  p, ci, dateLabel, recordKey, onAttended, onNoShow, onDismiss, onBusyChange,
}) => {
  type Pending =
    | { kind: 'attended'; anchorRect: DOMRect }
    | { kind: 'no-show'; anchorRect: DOMRect }
    | { kind: 'dismiss'; anchorRect: DOMRect };
  const [pending, setPending] = useState<Pending | null>(null);

  // A session that's been signed in but not signed out. These rows offer a
  // "Sign out" action (rather than the generic "Attended") since the arrival
  // is already on record — only the departure is missing.
  const isPartialSignIn = !!p.attendance?.actualArrival && !p.attendance?.actualDeparture;

  useEffect(() => {
    onBusyChange(recordKey, pending !== null);
    return () => { onBusyChange(recordKey, false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, recordKey]);

  // Fast path: clicking the wide green "Attended" body marks the session
  // attended at the scheduled times with no further confirmation. The
  // small "…" button at the right edge opens the AttendedTimesModal so
  // the user can edit times/comments before saving. For partial sign-ins
  // we preserve the recorded arrival and only fill in the missing
  // departure with the scheduled end.
  const handleAttendedQuick = () => {
    const arrival = p.attendance?.actualArrival ?? p.scheduledStart;
    const departure = p.attendance?.actualDeparture ?? p.scheduledEnd;
    const arrivalComment = p.attendance?.arrivalComment ?? null;
    const departureComment = p.attendance?.departureComment ?? null;
    onAttended(p, arrival, departure, arrivalComment, departureComment);
  };

  const handleAttendedEditClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setPending({ kind: 'attended', anchorRect: e.currentTarget.getBoundingClientRect() });

  const handleNoShowClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setPending({ kind: 'no-show', anchorRect: e.currentTarget.getBoundingClientRect() });

  const handleDismissClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setPending({ kind: 'dismiss', anchorRect: e.currentTarget.getBoundingClientRect() });

  const handleAttendedConfirm = (
    arrival: string,
    departure: string,
    arrivalComment: string | null,
    departureComment: string | null,
  ) => {
    onAttended(p, arrival, departure, arrivalComment, departureComment);
    setPending(null);
  };

  const handleConfirm = (comment: string | null, reason: string | null) => {
    if (!pending) return;
    if (pending.kind === 'no-show') {
      const merged = reason ? composeNoShowComment(reason, comment ?? '') : (comment || null);
      onNoShow(p, merged);
    } else if (pending.kind === 'dismiss') {
      // Match the WeeklySchedule CancelDateModal merge: "Other" lets the
      // freetext stand alone; otherwise we join "{preset} — {freetext}"
      // so the audit trail captures both the bucket and the nuance.
      let merged: string | null;
      if (reason === 'Other') merged = comment;
      else if (reason && comment) merged = `${reason} — ${comment}`;
      else merged = reason || comment || null;
      onDismiss(p, merged);
    }
    setPending(null);
  };

  return (
    <div
      className="dash-actionreq-row"
      style={{ ['--child-color' as string]: ci.colour } as React.CSSProperties}
    >
      <div className="dash-actionreq-row__when">
        <span className="dash-actionreq-row__date">{dateLabel}</span>
        <span className="dash-actionreq-row__time">{p.scheduledStart}–{p.scheduledEnd}</span>
      </div>
      <div className="dash-actionreq-row__child">
        <span className="dash-actionreq-row__avatar">
          {ci.profile ? (
            <ChildAvatar
              child={ci.profile}
              size={36}
              imageUrl={ci.profile.profile_image_thumbnail_url}
              showBadge={true}
            />
          ) : (
            <span className="dash-actionreq-row__avatar-fallback">{ci.initials}</span>
          )}
        </span>
        <div className="dash-actionreq-row__namestack">
          <span className="dash-actionreq-row__name">{ci.name}</span>
          {isPartialSignIn && (
            <span className="dash-actionreq-row__partial dash-actionreq-row__partial--signedin">
              <i className="fas fa-check-circle" /> Signed in at {p.attendance!.actualArrival} · sign-out missing
            </span>
          )}
        </div>
      </div>
      <div className="dash-actionreq-row__actions">
        <div className="dash-actionreq-row__attended">
          <button
            type="button"
            className="dash-actionreq-row__attended-main"
            onClick={handleAttendedQuick}
            title={isPartialSignIn
              ? 'Sign out at the scheduled end time'
              : 'Mark attended — sign-in and sign-out at the scheduled times'}
          >
            {isPartialSignIn ? (
              <><i className="fas fa-door-open" /> Sign out</>
            ) : (
              <><i className="fas fa-check-double" /> Attended</>
            )}
          </button>
          <button
            type="button"
            className="dash-actionreq-row__attended-edit"
            onClick={handleAttendedEditClick}
            title={isPartialSignIn ? 'Edit sign-out time' : 'Edit arrival + departure times'}
            aria-label={isPartialSignIn ? 'Edit sign-out time' : 'Edit attendance times'}
          >
            <i className="fas fa-ellipsis-h" />
          </button>
        </div>
        <button
          type="button"
          className="dash-actionreq-row__cta dash-actionreq-row__cta--noshow"
          onClick={handleNoShowClick}
        >
          <i className="fas fa-user-slash" /> No-show
        </button>
        <button
          type="button"
          className="dash-actionreq-row__cta dash-actionreq-row__cta--dismiss"
          onClick={handleDismissClick}
          title="Cancel this session as a one-off (e.g. bank holiday, school closed)"
        >
          <i className="fas fa-ban" /> Cancel session
        </button>
      </div>
      <AttendedTimesModal
        anchorRect={pending?.kind === 'attended' ? pending.anchorRect : null}
        childName={ci.name}
        scheduledStart={p.scheduledStart}
        scheduledEnd={p.scheduledEnd}
        defaultArrival={p.attendance?.actualArrival ?? p.scheduledStart}
        defaultDeparture={p.attendance?.actualDeparture ?? p.scheduledEnd}
        defaultArrivalComment={p.attendance?.arrivalComment ?? ''}
        defaultDepartureComment={p.attendance?.departureComment ?? ''}
        isCompletingSignIn={!!p.attendance?.actualArrival && !p.attendance?.actualDeparture}
        onConfirm={handleAttendedConfirm}
        onCancel={() => setPending(null)}
      />
      <AttendanceConfirmModal
        anchorRect={
          pending && pending.kind !== 'attended' ? pending.anchorRect : null
        }
        kind={
          (pending && pending.kind !== 'attended'
            ? pending.kind
            : 'no-show') as AttendanceActionKind
        }
        childName={ci.name}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

const Dashboard: React.FC<Props> = ({ childProfiles, onViewChildPosts }) => {
  const { activeUser, orgConfig } = useAppState();
  const graceMinutes = orgConfig?.attendanceGraceMinutes ?? DEFAULT_GRACE_MINUTES;

  // Whitelist-aware set of child IDs the current user is allowed to see.
  // Home.fetchChildProfiles has already filtered `childProfiles` to the
  // user's whitelist (when one applies), so deriving from it gives us the
  // pre-filter we want for *all* schedule/attendance data. Admins /
  // unrestricted users get the full org list, so this is a no-op for them.
  const allowedChildIds = useMemo(
    () => new Set(childProfiles.map(c => c.id)),
    [childProfiles],
  );

  // ── Data ──────────────────────────────────────
  const [sessions, setSessions] = useState<ScheduleSession[]>([]);
  // True once hydrateSchedules has returned at least once. Until then
  // `sessions` is empty for reasons that have nothing to do with the data, and
  // anything derived from it must not be treated as the real picture.
  const [schedulesHydrated, setSchedulesHydrated] = useState(false);
  const [cancelledExceptionDates, setCancelledExceptionDates] = useState<Map<string, Set<string>>>(new Map());
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [staff, setStaff] = useState<StaffProfile[]>([]);

  // Today re-evaluated from `now` so a midnight rollover automatically
  // re-fetches and re-projects against the new date.
  const [now, setNow] = useState<Date>(new Date());
  const today = toDateString(now);

  // ── Add unscheduled session modal ───────────────────
  const [showAddModal, setShowAddModal] = useState(false);

  // Stable id derivation: keying by serverId means the React key for a card
  // does not change between hydration and silent refreshes, so cards (and
  // their internal modal state) survive a refresh.
  const localIdForServer = (serverId: number) => `dash-session-${serverId}`;

  const createUnscheduledSession = async (data: {
    childId: number;
    startTime: string;
    endTime: string;
  }) => {
    const dayName = weekdayOf(toDateString(now));
    const teacherIds = activeUser ? [activeUser.id] : [];
    try {
      const { id: serverId } = await ScheduleDataService.createScheduleItem({
        childId: data.childId,
        dayOfWeek: dayName,
        startTime: data.startTime,
        endTime: data.endTime,
        teacherIds,
        locationIds: [],
        frequency: 'once',
        startDate: today,
        endDate: today,
      });
      // Append to local state so it appears immediately in the timeline.
      const localId = localIdForServer(serverId);
      setSessions(prev => [
        ...prev,
        {
          id: localId, lane: 0,
          dayOfWeek: dayName,
          startTime: data.startTime, endTime: data.endTime,
          childId: data.childId,
          teacherIds: [...teacherIds],
          locationIds: [],
          frequency: 'once',
          startDate: today, endDate: today,
          serverId,
        },
      ]);
      setShowAddModal(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create unscheduled session', err);
    }
  };

  // ── Just-me toggle (defaults to off for everyone; persisted overrides) ──
  const [justMe, setJustMe] = useState<boolean>(() => {
    const saved = localStorage.getItem(JUST_ME_STORAGE_KEY);
    return saved === 'true';
  });
  useEffect(() => {
    localStorage.setItem(JUST_ME_STORAGE_KEY, String(justMe));
  }, [justMe]);

  // ── Needs-attention tray ────────────────────────────
  // Collapsed on every load. The bar alone answers "is there a backlog, how
  // big, whose?" — opening it is a deliberate step into doing the work, and
  // an unrecorded session from last Tuesday shouldn't push today's Now card
  // off the screen before the user has even asked to see it.
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [dismissedAttention, setDismissedAttention] =
    useState<Set<string>>(loadDismissedAttention);
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);

  // Tick the clock for slot/countdown updates and midnight rollover.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Fetch staff profiles (used to render teacher names on the cards).
  useEffect(() => {
    let cancelled = false;
    UserDataService.fetchStaffProfiles()
      .then(data => { if (!cancelled) setStaff(data); })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch staff for dashboard', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Tracks NowCards that currently have a confirm modal open. While non-empty
  // the silent refresh is suppressed so a mid-typed reason is never dropped.
  const busyKeysRef = useRef<Set<string>>(new Set());
  const handleBusyChange = useCallback((key: string, busy: boolean) => {
    const set = busyKeysRef.current;
    if (busy) set.add(key); else set.delete(key);
  }, []);

  // Hydrate schedules/exceptions and apply to state. Used on mount and on
  // the silent refresh interval. Keeping the merge logic in one place means
  // both paths produce identical state shape — no special-casing.
  const hydrateSchedules = useCallback(async () => {
    const [items, exceptions] = await Promise.all([
      ScheduleDataService.listScheduleItems(),
      ScheduleDataService.listScheduleExceptions(),
    ]);

    const newSessions: ScheduleSession[] = items.map((it) => ({
      id: localIdForServer(it.serverId), lane: 0,
      dayOfWeek: it.dayOfWeek,
      startTime: it.startTime, endTime: it.endTime,
      childId: it.childId,
      teacherIds: [...it.teacherIds],
      locationIds: [...it.locationIds],
      frequency: it.frequency,
      startDate: it.startDate, endDate: it.endDate,
      serverId: it.serverId,
    }));

    const cancelMap = new Map<string, Set<string>>();
    for (const ex of exceptions) {
      if (ex.status !== 'cancelled') continue;
      const localId = localIdForServer(ex.scheduleItemServerId);
      const dates = cancelMap.get(localId) ?? new Set<string>();
      dates.add(ex.date);
      cancelMap.set(localId, dates);
    }
    setSessions(newSessions);
    setCancelledExceptionDates(cancelMap);
    setSchedulesHydrated(true);
  }, []);

  // Refresh attendance over the LOOKBACK_DAYS window so the action-required
  // tray can identify past sessions that are still missing attendance, while
  // also keeping today's slots up to date.
  const refreshAttendance = useCallback(async (todayStr: string) => {
    const todayD = new Date(todayStr + 'T00:00');
    const fromD = new Date(todayD);
    fromD.setDate(fromD.getDate() - LOOKBACK_DAYS);
    const rows = await AttendanceDataService.listAttendance(isoDate(fromD), todayStr);
    setAttendance(rows);
  }, []);

  // Initial hydrate of schedules + exceptions.
  useEffect(() => {
    let cancelled = false;
    hydrateSchedules().catch(err => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.error('Failed to hydrate schedules for dashboard', err);
    });
    return () => { cancelled = true; };
  }, [hydrateSchedules]);

  // Initial + midnight-rollover attendance fetch.
  useEffect(() => {
    let cancelled = false;
    refreshAttendance(today).catch(err => {
      if (cancelled) return;
      // eslint-disable-next-line no-console
      console.error('Failed to fetch attendance for dashboard', err);
    });
    return () => { cancelled = true; };
  }, [today, refreshAttendance]);

  // Silent server refresh — picks up schedule edits, cancellations, and
  // attendance entered by other users. Skipped while any confirm modal is
  // open so the user's in-progress action survives. Failures are swallowed
  // (we keep showing cached data and try again next tick). Paused while the
  // API can't be reached — connectivity runs its own recovery probe — and
  // fired straight away when it comes back, rather than up to 20s later.
  const { status: connectivity } = useConnectivity();
  const online = connectivity === 'online';
  const wasOnlineRef = useRef(online);
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (cancelled) return;
      if (busyKeysRef.current.size > 0) return;
      Promise.all([
        hydrateSchedules(),
        refreshAttendance(today),
      ]).catch(err => {
        // eslint-disable-next-line no-console
        console.warn('Silent dashboard refresh failed', err);
      });
    };
    const cameBack = online && !wasOnlineRef.current;
    wasOnlineRef.current = online;
    if (!online) return () => { cancelled = true; };
    if (cameBack) refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [today, hydrateSchedules, refreshAttendance, online]);

  // ── Project sessions into slots ──────────────────────

  const projectedSessions: DashboardSession[] = useMemo(() => {
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const all: DashboardSession[] = [];

    // Today's projection — full slot assignment.
    for (const s of sessions) {
      if (s.childId == null || !allowedChildIds.has(s.childId)) continue;
      if (!occursOnDate(s, new Date(today + 'T00:00'))) continue;
      if (cancelledExceptionDates.get(s.id)?.has(today)) continue;
      if (justMe && activeUser && !s.teacherIds.includes(activeUser.id)) continue;

      const att = attendance.find(
        a =>
          a.scheduleItemServerId === s.serverId &&
          a.childId === s.childId &&
          a.attendanceDate === today,
      ) ?? null;
      const startMins = timeToMins(s.startTime);
      const endMins = timeToMins(s.endTime);
      // Slot — mirrors the iOS DashboardView.slot(for:) decision tree so
      // the two apps surface the same things in the same buckets:
      //   • "now"        a signed-in session that hasn't been signed out
      //                  (no stale timeout — stays here until the user
      //                  signs out), OR the scheduled window [start, end +
      //                  NOW_STALE_GRACE_MIN] when nothing has been logged.
      //   • "next"       upcoming, with start within NEXT_UP_WINDOW_MIN.
      //   • "unactioned" past scheduled end + grace, no attendance row at
      //                  all — surfaces in "Requires attention" so the user
      //                  can record what happened.
      //   • "other"      everything else (e.g. already marked no-show, or
      //                  fully attended and past). Not rendered on the
      //                  dashboard but kept for the timeline strip.
      const tappedIn = !!att?.actualArrival;
      const tappedOut = !!att?.actualDeparture;
      const noShow = !!att && !att.actualArrival && !att.actualDeparture;
      const stale = nowMins > endMins + NOW_STALE_GRACE_MIN;
      const inNowWindow = nowMins >= startMins && nowMins <= endMins + NOW_STALE_GRACE_MIN;
      const isSignedInAndOpen = tappedIn && !tappedOut && nowMins >= startMins;
      const isInProgress =
        isSignedInAndOpen ||
        (inNowWindow && !tappedOut && !noShow);

      let slot: Slot;
      if (isInProgress) slot = 'now';
      else if (nowMins < startMins && startMins - nowMins <= NEXT_UP_WINDOW_MIN) slot = 'next';
      else if (stale && !att) slot = 'unactioned';
      else slot = 'other';

      all.push({
        session: s,
        attendanceDate: today,
        scheduledStart: s.startTime, scheduledEnd: s.endTime,
        startMins, endMins, attendance: att, slot,
      });
    }

    // Past-day unactioned scan: walk back LOOKBACK_DAYS, project every rule,
    // and emit any occurrence that has no attendance row and no cancellation.
    // These surface in the action-required tray so old gaps don't fall off the
    // user's radar.
    const todayD = new Date(today + 'T00:00');
    for (let i = 1; i <= LOOKBACK_DAYS; i++) {
      const d = new Date(todayD);
      d.setDate(d.getDate() - i);
      const dateStr = isoDate(d);

      for (const s of sessions) {
        if (s.childId == null || !allowedChildIds.has(s.childId)) continue;
        if (!occursOnDate(s, new Date(dateStr + 'T00:00'))) continue;
        if (cancelledExceptionDates.get(s.id)?.has(dateStr)) continue;
        if (justMe && activeUser && !s.teacherIds.includes(activeUser.id)) continue;

        const att = attendance.find(
          a =>
            a.scheduleItemServerId === s.serverId &&
            a.childId === s.childId &&
            a.attendanceDate === dateStr,
        ) ?? null;

        // Skip rows already fully recorded (arrival + departure both set)
        // or marked no-show. Keep rows with no attendance OR with a partial
        // sign-in (arrival without departure) — both need user action.
        if (att) {
          const isPartialSignIn = !!att.actualArrival && !att.actualDeparture;
          if (!isPartialSignIn) continue;
        }

        const startMins = timeToMins(s.startTime);
        const endMins = timeToMins(s.endTime);
        all.push({
          session: s,
          attendanceDate: dateStr,
          scheduledStart: s.startTime, scheduledEnd: s.endTime,
          startMins, endMins, attendance: att, slot: 'unactioned',
        });
      }
    }

    // Sort: oldest unactioned first, then today's slots by start time.
    all.sort((a, b) => {
      if (a.attendanceDate !== b.attendanceDate) {
        return a.attendanceDate.localeCompare(b.attendanceDate);
      }
      return a.startMins - b.startMins;
    });

    return all;
  }, [sessions, cancelledExceptionDates, attendance, today, now, justMe, activeUser, allowedChildIds]);

  // Partition for rendering. NOW / UP NEXT are scoped to TODAY, but the
  // "Needs attention" tray spans the whole LOOKBACK_DAYS window: any past
  // session (within that window) with missing or partial attendance stays
  // listed until it's completed, marked no-show, or cancelled. The rows are
  // already projected oldest-first in projectedSessions.
  const todaysProjected = projectedSessions.filter(p => p.attendanceDate === today);
  const allUnactioned = projectedSessions.filter(p => p.slot === 'unactioned');
  // "Dismiss all" only hides rows — it never records anything — so the tray
  // shows whatever the user hasn't cleared. A gap that appears *after* a
  // dismissal (a later date, or a schedule edited retroactively) is a new
  // key and surfaces normally.
  const unactionedSessions = allUnactioned.filter(
    p => !dismissedAttention.has(attentionRowKey(p)),
  );
  const nowSessions = todaysProjected.filter(p => p.slot === 'now');
  // NEXT_UP_WINDOW_MIN is already applied during slot assignment — anything
  // marked 'next' is within the window.
  const nextSessions = todaysProjected.filter(p => p.slot === 'next');

  const sectionsEmpty =
    unactionedSessions.length === 0 &&
    nowSessions.length === 0 &&
    nextSessions.length === 0;

  // Drop dismissed keys once their session stops being unactioned (recorded,
  // marked no-show, cancelled, or simply aged past LOOKBACK_DAYS) so the
  // stored set can't grow without bound. Keyed on a fingerprint string so
  // this only fires when the underlying list actually changes.
  const unactionedFingerprint = allUnactioned.map(attentionRowKey).join('|');
  useEffect(() => {
    // Not before the schedules land. On mount `sessions` is [], so every key
    // would look like it had stopped being unactioned and the whole set would
    // be pruned — and saved — wiping the user's dismissals on every page load.
    if (!schedulesHydrated) return;
    setDismissedAttention(prev => {
      if (prev.size === 0) return prev;
      const live = new Set(unactionedFingerprint ? unactionedFingerprint.split('|') : []);
      const kept = Array.from(prev).filter(k => live.has(k));
      if (kept.length === prev.size) return prev;
      const next = new Set(kept);
      saveDismissedAttention(next);
      return next;
    });
  }, [unactionedFingerprint, schedulesHydrated]);


  // ── Tap-in / tap-out wiring ──────────────────────────

  // Manage in-flight saves so we don't double-fire when the network is slow.
  // Key includes the session's attendanceDate so retroactive sign-ins for an
  // unactioned past day don't collide with today's pending record.
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const recordKeyFor = attentionRowKey;

  const beginPending = (key: string) =>
    setPendingKeys(prev => new Set(prev).add(key));
  const endPending = (key: string) =>
    setPendingKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  const recordTapIn = async (
    p: DashboardSession,
    actualArrival: string,
    comment: string | null,
  ) => {
    if (p.session.serverId === undefined || p.session.childId == null) return;
    const date = p.attendanceDate;
    const key = recordKeyFor(p);
    if (pendingKeys.has(key)) return;
    beginPending(key);
    try {
      if (p.attendance?.serverId !== undefined) {
        await AttendanceDataService.updateAttendance(p.attendance.serverId, date, {
          actualArrival,
          arrivalComment: comment,
        });
        setAttendance(prev =>
          prev.map(a =>
            a.scheduleItemServerId === p.session.serverId &&
            a.childId === p.session.childId &&
            a.attendanceDate === date
              ? { ...a, actualArrival, arrivalComment: comment }
              : a,
          ),
        );
      } else {
        const { id } = await AttendanceDataService.createAttendance({
          scheduleItemServerId: p.session.serverId,
          childId: p.session.childId,
          attendanceDate: date,
          actualArrival,
          actualDeparture: null,
          arrivalComment: comment,
          departureComment: null,
        });
        setAttendance(prev => [
          ...prev,
          {
            serverId: id,
            scheduleItemServerId: p.session.serverId!,
            childId: p.session.childId!,
            attendanceDate: date,
            actualArrival,
            actualDeparture: null,
            arrivalComment: comment,
            departureComment: null,
          },
        ]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to record tap-in', err);
    } finally {
      endPending(key);
    }
  };

  const recordTapOut = async (
    p: DashboardSession,
    actualDeparture: string,
    comment: string | null,
  ) => {
    if (p.attendance?.serverId === undefined) return;
    const date = p.attendanceDate;
    const key = recordKeyFor(p);
    if (pendingKeys.has(key)) return;
    beginPending(key);
    try {
      await AttendanceDataService.updateAttendance(p.attendance.serverId, date, {
        actualDeparture,
        departureComment: comment,
      });
      setAttendance(prev =>
        prev.map(a =>
          a.scheduleItemServerId === p.session.serverId &&
          a.childId === p.session.childId &&
          a.attendanceDate === date
            ? { ...a, actualDeparture, departureComment: comment }
            : a,
        ),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to record tap-out', err);
    } finally {
      endPending(key);
    }
  };

  const recordNoShow = async (p: DashboardSession, comment: string | null) => {
    if (p.session.serverId === undefined || p.session.childId == null) return;
    const date = p.attendanceDate;
    const key = recordKeyFor(p);
    if (pendingKeys.has(key)) return;
    beginPending(key);
    try {
      if (p.attendance?.serverId !== undefined) {
        await AttendanceDataService.updateAttendance(p.attendance.serverId, date, {
          actualArrival: null,
          actualDeparture: null,
          arrivalComment: comment,
          departureComment: null,
        });
        setAttendance(prev =>
          prev.map(a =>
            a.scheduleItemServerId === p.session.serverId &&
            a.childId === p.session.childId &&
            a.attendanceDate === date
              ? { ...a, actualArrival: null, actualDeparture: null, arrivalComment: comment, departureComment: null }
              : a,
          ),
        );
      } else {
        const { id } = await AttendanceDataService.createAttendance({
          scheduleItemServerId: p.session.serverId,
          childId: p.session.childId,
          attendanceDate: date,
          actualArrival: null,
          actualDeparture: null,
          arrivalComment: comment,
          departureComment: null,
        });
        setAttendance(prev => [
          ...prev,
          {
            serverId: id,
            scheduleItemServerId: p.session.serverId!,
            childId: p.session.childId!,
            attendanceDate: date,
            actualArrival: null,
            actualDeparture: null,
            arrivalComment: comment,
            departureComment: null,
          },
        ]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to record no-show', err);
    } finally {
      endPending(key);
    }
  };

  // Mark a past session attended with user-supplied arrival + departure
  // times and per-side comments. Used by the action-required tray's
  // "Attended" CTA, which opens the AttendedTimesModal to gather the
  // values (defaulted to the scheduled times, or to the existing partial
  // sign-in if there is one). Updates any existing partial record in
  // place rather than creating a duplicate.
  const recordAttended = async (
    p: DashboardSession,
    arrival: string,
    departure: string,
    arrivalComment: string | null,
    departureComment: string | null,
  ) => {
    if (p.session.serverId === undefined || p.session.childId == null) return;
    const date = p.attendanceDate;
    const key = recordKeyFor(p);
    if (pendingKeys.has(key)) return;
    beginPending(key);
    try {
      if (p.attendance?.serverId !== undefined) {
        await AttendanceDataService.updateAttendance(p.attendance.serverId, date, {
          actualArrival: arrival,
          actualDeparture: departure,
          arrivalComment,
          departureComment,
        });
        setAttendance(prev =>
          prev.map(a =>
            a.scheduleItemServerId === p.session.serverId &&
            a.childId === p.session.childId &&
            a.attendanceDate === date
              ? {
                  ...a,
                  actualArrival: arrival,
                  actualDeparture: departure,
                  arrivalComment,
                  departureComment,
                }
              : a,
          ),
        );
      } else {
        const { id } = await AttendanceDataService.createAttendance({
          scheduleItemServerId: p.session.serverId,
          childId: p.session.childId,
          attendanceDate: date,
          actualArrival: arrival,
          actualDeparture: departure,
          arrivalComment,
          departureComment,
        });
        setAttendance(prev => [
          ...prev,
          {
            serverId: id,
            scheduleItemServerId: p.session.serverId!,
            childId: p.session.childId!,
            attendanceDate: date,
            actualArrival: arrival,
            actualDeparture: departure,
            arrivalComment,
            departureComment,
          },
        ]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to record attendance', err);
    } finally {
      endPending(key);
    }
  };

  // Dismiss an unactioned past session by writing a one-off "cancelled"
  // exception for that date. The row clears from the action-required tray
  // because the next projection will skip the session for that date.
  const recordDismiss = async (p: DashboardSession, reason: string | null) => {
    if (p.session.serverId === undefined) return;
    const date = p.attendanceDate;
    const key = recordKeyFor(p);
    if (pendingKeys.has(key)) return;
    beginPending(key);
    try {
      await ScheduleDataService.createScheduleException({
        scheduleItemServerId: p.session.serverId,
        exceptionDate: date,
        status: 'cancelled',
        reason: reason ?? undefined,
      });
      setCancelledExceptionDates(prev => {
        const next = new Map(prev);
        const dates = new Set(next.get(p.session.id) ?? []);
        dates.add(date);
        next.set(p.session.id, dates);
        return next;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to dismiss session', err);
    } finally {
      endPending(key);
    }
  };

  // ── Render helpers ────────────────────────────────────

  const childInfo = (id: number | null): ChildInfoView => {
    const c = id != null ? childProfiles.find(cp => cp.id === id) ?? null : null;
    if (!c) return { profile: null, name: '—', colour: '#aaaaaa', initials: '?' };
    return {
      profile: c,
      name: `${c.first_name} ${c.last_name}`,
      colour: c.colour_code || '#aaaaaa',
      initials: c.initials ?? '?',
    };
  };

  // ── Needs-attention bar (collapsed summary) ───────────

  const attentionSpan = formatDateSpan(
    unactionedSessions.map(p => p.attendanceDate),
    today,
  );

  // One face per child, in the order their oldest gap appears, capped so the
  // pile can't crowd the controls on a narrow bar. Cheap enough to derive on
  // each render — memoising it would only invite the cache and the filtered
  // list to drift apart.
  const PILE_LIMIT = 4;
  const attentionChildren = (() => {
    const seen = new Map<number | string, DashboardSession>();
    unactionedSessions.forEach(p => {
      const id = p.session.childId ?? `unknown-${p.session.id}`;
      if (!seen.has(id)) seen.set(id, p);
    });
    return Array.from(seen.entries());
  })();

  const attentionFaces = attentionChildren.slice(0, PILE_LIMIT).map(([id, p]) => {
    const ci = childInfo(p.session.childId);
    return {
      key: String(id),
      name: ci.name,
      colour: ci.colour,
      initials: ci.initials,
      thumbnail: ci.profile?.profile_image_thumbnail_url ?? null,
    };
  });
  const attentionOverflow = Math.max(0, attentionChildren.length - PILE_LIMIT);

  // "Dismiss all" clears the listed rows from the dashboard only — no
  // attendance is written and no session is cancelled, so the same gaps stay
  // on the Attendance page. The confirm dialog says so explicitly, because
  // "dismiss" next to a row-level "Cancel session" button could otherwise be
  // read as doing something to the records.
  const handleDismissAll = () => {
    const next = new Set(unactionedSessions.map(attentionRowKey));
    setDismissedAttention(next);
    saveDismissedAttention(next);
    setConfirmDismissAll(false);
    setAttentionOpen(false);
  };

  // Hold the silent refresh while the confirm dialog is up, matching the
  // row-level modals — a re-render underneath would be jarring mid-decision.
  useEffect(() => {
    handleBusyChange('dismiss-all', confirmDismissAll);
    return () => { handleBusyChange('dismiss-all', false); };
  }, [confirmDismissAll, handleBusyChange]);

  // ── Render ──────────────────────────────────────────

  const today_label = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(now);

  return (
    <div className="dash-root">
      <div className="dash-header">
        <h2 className="dash-title">Dashboard</h2>
        <div className="dash-subtitle">{today_label}</div>
        <div className="dash-spacer" />
        <button
          type="button"
          className="dash-addbtn"
          data-help="dash.add-unscheduled"
          onClick={() => setShowAddModal(true)}
          title="Add an unscheduled session"
        >
          <i className="fas fa-plus" /> Add unscheduled
        </button>
        <label className="dash-toggle" data-help="dash.just-me">
          <input
            type="checkbox"
            checked={justMe}
            onChange={(e) => setJustMe(e.target.checked)}
          />
          <span>Just me</span>
        </label>
      </div>

      <UnscheduledSessionModal
        open={showAddModal}
        childProfiles={childProfiles}
        now={now}
        sessionIntervalMin={orgConfig?.defaultSessionIntervalMinutes ?? ORG_CONFIG_DEFAULTS.defaultSessionIntervalMinutes}
        onConfirm={createUnscheduledSession}
        onCancel={() => setShowAddModal(false)}
      />

      {/* Today's schedule strip — read-only, single day. Same visual family
          as the Weekly Schedule grid but flattened to one day and trimmed of
          all editing affordances. Session bars are positioned absolutely by
          time and stacked into lanes when overlapping (same rule as the main
          schedule). The amber now-line marks the current moment. */}
      {(() => {
        const todayAll = projectedSessions.filter(p => p.attendanceDate === today);
        if (todayAll.length === 0) return null;

        const parseHM = (t: string) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
        };
        const dayStartStr = orgConfig?.workingDayStart ?? '09:00';
        const dayEndStr   = orgConfig?.workingDayEnd   ?? '17:00';
        const startMins   = parseHM(dayStartStr);
        const endMins     = parseHM(dayEndStr);
        const totalMins   = Math.max(60, endMins - startMins);

        // Lane assignment — sort by start time, then drop each session into
        // the lowest-index lane whose last occupant ends at or before this
        // one starts. New lanes are created on demand.
        const LANE_HEIGHT  = 30;
        const LANE_GAP     = 3;
        const TRACK_PAD_Y  = 4;
        const sorted = [...todayAll].sort((a, b) => a.startMins - b.startMins);
        const laneEnds: number[] = [];
        const laneOf = new Map<string, number>();
        for (const p of sorted) {
          const key = `${p.session.id}-${p.scheduledStart}`;
          let placed = -1;
          for (let i = 0; i < laneEnds.length; i++) {
            if (laneEnds[i] <= p.startMins) { placed = i; break; }
          }
          if (placed === -1) {
            placed = laneEnds.length;
            laneEnds.push(p.endMins);
          } else {
            laneEnds[placed] = p.endMins;
          }
          laneOf.set(key, placed);
        }
        const laneCount = Math.max(1, laneEnds.length);
        const trackHeight = TRACK_PAD_Y * 2 + laneCount * LANE_HEIGHT + (laneCount - 1) * LANE_GAP;
        // 30-min slot count drives the dashed gridlines in the track
        // background (same pattern as the main schedule grid).
        const slotCount = Math.max(1, Math.round(totalMins / 30));

        const nowMins = now.getHours() * 60 + now.getMinutes();
        const showNow = nowMins >= startMins && nowMins <= endMins;
        const nowPct  = ((nowMins - startMins) / totalMins) * 100;

        const firstHour = Math.ceil(startMins / 60);
        const lastHour  = Math.floor(endMins / 60);
        const hours: number[] = [];
        for (let h = firstHour; h <= lastHour; h++) hours.push(h);

        return (
          <div className="dash-today-schedule" data-help="dash.timeline">
            <div className="dash-today-schedule__heading">
              <span className="dash-today-schedule__title">Today's schedule</span>
              <span className="dash-today-schedule__range">{dayStartStr} – {dayEndStr}</span>
            </div>
            <div className="dash-today-schedule__inner">
              <div className="dash-today-schedule__timebar">
                <div className="dash-today-schedule__timebar-inner">
                  {hours.map(h => {
                    const pct = ((h * 60 - startMins) / totalMins) * 100;
                    return (
                      <span
                        key={h}
                        className="dash-today-schedule__hour"
                        style={{ left: `${pct}%` }}
                      >
                        {String(h).padStart(2, '0')}:00
                      </span>
                    );
                  })}
                </div>
              </div>
              <div
                className="dash-today-schedule__track"
                style={{
                  height: `${trackHeight}px`,
                  ['--dash-slot-count' as string]: slotCount,
                }}
              >
                <div className="dash-today-schedule__track-inner">
                {sorted.map(p => {
                  const ci     = childInfo(p.session.childId);
                  const key    = `${p.session.id}-${p.scheduledStart}`;
                  const lane   = laneOf.get(key) ?? 0;
                  const left   = Math.max(0, ((p.startMins - startMins) / totalMins) * 100);
                  const width  = Math.max(0.5, ((p.endMins - p.startMins) / totalMins) * 100);
                  const top    = TRACK_PAD_Y + lane * (LANE_HEIGHT + LANE_GAP);
                  const colour = ci.colour;

                  // Visual state derived from attendance — same rules as
                  // computeStatus() in WeeklyAttendance.
                  const att        = p.attendance;
                  const isNoShow   = !!(att && !att.actualArrival && !att.actualDeparture);
                  const isAttended = !!(att && att.actualArrival);

                  let variantStyle: React.CSSProperties;
                  if (isAttended) {
                    // Match the attendance grid attended overlay.
                    variantStyle = {
                      background: `linear-gradient(180deg, ${colour}cc 0%, ${colour}99 100%)`,
                      border: `1.5px solid ${colour}`,
                      color: '#fff',
                      textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                    };
                  } else if (isNoShow) {
                    // Match the attendance grid no-show pattern.
                    variantStyle = {
                      background: `repeating-linear-gradient(135deg, #d63b3b22 0 6px, #d63b3b10 6px 12px)`,
                      border: `1px solid #d63b3b80`,
                      color: '#a02525',
                      textShadow: 'none',
                      textDecoration: 'line-through',
                    };
                  } else {
                    // Match the weekly schedule scheduled session bar.
                    variantStyle = {
                      background: `linear-gradient(180deg, ${colour}52 0%, ${colour}38 100%)`,
                      boxShadow: `inset 0 0 0 1px ${colour}60`,
                      color: colour,
                      textShadow: '0 1px 0 rgba(255, 255, 255, 0.5)',
                    };
                  }

                  return (
                    <div
                      key={key}
                      className="dash-today-schedule__session"
                      style={{
                        left:   `${left}%`,
                        width:  `${width}%`,
                        top:    `${top}px`,
                        height: `${LANE_HEIGHT}px`,
                        ...variantStyle,
                      }}
                      title={`${ci.name} · ${p.scheduledStart}–${p.scheduledEnd}${
                        isAttended ? ' · attended' : isNoShow ? ' · no-show' : ''
                      }`}
                    >
                      {isAttended && (
                        <span className="dash-today-schedule__session-check" aria-hidden="true">
                          <i className="fas fa-check" />
                        </span>
                      )}
                      <span className="dash-today-schedule__session-initials">{ci.initials}</span>
                    </div>
                  );
                })}
                {showNow && (
                  <div
                    className="dash-today-schedule__now"
                    style={{ left: `${nowPct}%` }}
                    aria-hidden="true"
                  />
                )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {sectionsEmpty ? (
        // iOS-style empty state. Shown when none of the three sections has
        // anything to render — i.e. no past gaps, no current session, no
        // imminent session. Copy mirrors DashboardView.swift.
        <div className="dash-empty-state">
          <i className="far fa-calendar dash-empty-state__icon" aria-hidden />
          <div className="dash-empty-state__title">Nothing on right now</div>
          <div className="dash-empty-state__sub">
            Current sessions and anything starting in the next 30 minutes will appear here.
          </div>
        </div>
      ) : (
        <>
          {unactionedSessions.length > 0 && (
            <section className="dash-section dash-section--attention" data-help="dash.attention">
              <h3 className="dash-section__header">Needs attention</h3>
              <div className={`dash-actionreq${attentionOpen ? ' is-open' : ''}`}>
                <div className="dash-actionreq__bar">
                  <button
                    type="button"
                    className="dash-actionreq__toggle"
                    onClick={() => setAttentionOpen(o => !o)}
                    aria-expanded={attentionOpen}
                    aria-controls="dash-actionreq-list"
                  >
                    <span className="dash-actionreq__count" aria-hidden>
                      {unactionedSessions.length}
                    </span>
                    <span className="dash-actionreq__headtext">
                      <span className="dash-actionreq__title">
                        Attendance not recorded
                      </span>
                      <span className="dash-actionreq__meta">
                        {unactionedSessions.length === 1 ? '1 session' : `${unactionedSessions.length} sessions`}
                        {' · '}
                        {attentionSpan}
                      </span>
                    </span>
                  </button>

                  {/* Whose gaps these are — the usual reason to open the tray.
                      Redundant once the rows are on screen, so collapsed only. */}
                  {!attentionOpen && attentionFaces.length > 0 && (
                    <span className="dash-actionreq__pile" aria-hidden>
                      {attentionFaces.map(f => (
                        <span
                          key={f.key}
                          className="dash-actionreq__pile-face"
                          style={{ background: f.colour }}
                          title={f.name}
                        >
                          {f.thumbnail
                            ? <img src={f.thumbnail} alt="" />
                            : f.initials}
                        </span>
                      ))}
                      {attentionOverflow > 0 && (
                        <span className="dash-actionreq__pile-more">+{attentionOverflow}</span>
                      )}
                    </span>
                  )}

                  <button
                    type="button"
                    className="dash-actionreq__dismiss"
                    data-help="dash.dismiss-all"
                    onClick={() => setConfirmDismissAll(true)}
                    title="Clear these from the dashboard without recording anything"
                  >
                    Dismiss all
                  </button>

                  <button
                    type="button"
                    className="dash-actionreq__chev"
                    onClick={() => setAttentionOpen(o => !o)}
                    aria-expanded={attentionOpen}
                    aria-controls="dash-actionreq-list"
                    aria-label={attentionOpen ? 'Hide the sessions' : 'Show the sessions'}
                  >
                    <i className="fas fa-chevron-down" aria-hidden />
                  </button>
                </div>

                {attentionOpen && (
                  <div className="dash-actionreq__list" id="dash-actionreq-list">
                    {unactionedSessions.map(p => {
                      const ci = childInfo(p.session.childId);
                      const rKey = recordKeyFor(p);
                      return (
                        <ActionRequiredCard
                          key={`${p.session.id}-${p.attendanceDate}`}
                          p={p}
                          ci={ci}
                          dateLabel={formatDateRelative(p.attendanceDate, today)}
                          recordKey={rKey}
                          onAttended={recordAttended}
                          onNoShow={recordNoShow}
                          onDismiss={recordDismiss}
                          onBusyChange={handleBusyChange}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* NOW and UP NEXT are always here once the page has anything on it
              at all, so it keeps its shape through the day and nobody has to
              work out whether an area is missing or merely empty. A session
              signed out leaves NOW empty rather than removing it — which is
              the difference between "everyone is done" and "where did that
              go?". The whole-page empty state below still covers the case
              where all three have nothing. */}
          <section className="dash-section dash-section--now">
            <h3 className="dash-section__header">Now</h3>
            {nowSessions.length === 0 ? (
              <p className="dash-section__empty">No sessions running right now.</p>
            ) : (
              <div className="dash-section__list">
                {nowSessions.map(p => {
                  const ci = childInfo(p.session.childId);
                  const rKey = recordKeyFor(p);
                  return (
                    <div
                      key={p.session.id}
                      className="dash-tline-item dash-tline-item--card"
                      style={{ ['--child-color' as string]: ci.colour } as React.CSSProperties}
                    >
                      <NowCard
                        p={p}
                        ci={ci}
                        now={now}
                        graceMinutes={graceMinutes}
                        recordKey={rKey}
                        onTapIn={recordTapIn}
                        onTapOut={recordTapOut}
                        onNoShow={recordNoShow}
                        onBusyChange={handleBusyChange}
                        onViewPosts={
                          onViewChildPosts && p.session.childId != null
                            ? () => onViewChildPosts(String(p.session.childId))
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="dash-section dash-section--next">
            <h3 className="dash-section__header">Up next</h3>
            {nextSessions.length === 0 ? (
              <p className="dash-section__empty">Nothing starting in the next 30 minutes.</p>
            ) : (
              <div className="dash-section__list">
                {nextSessions.map(p => {
                  const ci = childInfo(p.session.childId);
                  const rKey = recordKeyFor(p);
                  return (
                    <div
                      key={p.session.id}
                      className="dash-tline-item dash-tline-item--card"
                      style={{ ['--child-color' as string]: ci.colour } as React.CSSProperties}
                    >
                      <NextCard
                        p={p}
                        ci={ci}
                        now={now}
                        graceMinutes={graceMinutes}
                        recordKey={rKey}
                        onTapIn={recordTapIn}
                        onTapOut={recordTapOut}
                        onNoShow={recordNoShow}
                        onBusyChange={handleBusyChange}
                        onViewPosts={
                          onViewChildPosts && p.session.childId != null
                            ? () => onViewChildPosts(String(p.session.childId))
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <ConfirmationModal
        show={confirmDismissAll}
        title="Dismiss all reminders?"
        message={
          `This clears ${unactionedSessions.length === 1
            ? 'the session'
            : `all ${unactionedSessions.length} sessions`} from the dashboard. Nothing is recorded — the attendance is still outstanding, and you can complete it any time from the Attendance page.\n\n` +
          'Any new gaps will still appear here.'
        }
        confirmText="Dismiss all"
        cancelText="Keep them"
        onConfirm={handleDismissAll}
        onCancel={() => setConfirmDismissAll(false)}
      />
    </div>
  );
};

export default Dashboard;
